<!--
  v3.5 (May 12) Part Detail card.

  Opens when the user picks a part from the header search bar (or any
  other "View part" link). Shows:
    - catalog info (number, name, manufacturer, brand, totals)
    - stock per warehouse, with per-row actions (Reserve / Transfer /
      Adjust / Movements)
    - last 50 movements across all warehouses
    - linked jobs (parts_request_items + active reservations)
-->
<template>
  <v-dialog v-model="visible" max-width="1100" scrollable persistent>
    <v-card>
      <v-card-title class="d-flex align-center pa-3">
        <v-icon class="me-2" color="primary">mdi-package-variant-closed</v-icon>
        <div class="flex-grow-1">
          <span v-if="data?.part">
            <strong>{{ data.part.part_number }}</strong>
            &mdash;
            {{ data.part.name }}
          </span>
          <span v-else>Loading...</span>
        </div>
        <v-btn icon="mdi-close" variant="text" density="comfortable" @click="close" />
      </v-card-title>
      <v-divider />

      <v-card-text class="pa-0" style="background: #fafafa">
        <div v-if="loading" class="pa-6 text-center">
          <v-progress-circular indeterminate />
        </div>

        <div v-else-if="data" class="pa-3 d-flex flex-column" style="gap:12px;">
          <!-- Top totals chips -->
          <v-card flat>
            <div class="pa-3 d-flex flex-wrap align-center" style="gap:12px;">
              <v-chip color="primary" variant="tonal" prepend-icon="mdi-warehouse">
                {{ data.totals.warehouse_count }} warehouse(s)
              </v-chip>
              <v-chip color="success" variant="tonal" prepend-icon="mdi-package">
                On Hand: {{ data.totals.on_hand }}
              </v-chip>
              <v-chip color="amber" variant="tonal" prepend-icon="mdi-lock-clock">
                Reserved: {{ data.totals.reserved }}
              </v-chip>
              <v-chip color="info" variant="tonal" prepend-icon="mdi-truck-fast">
                Available: {{ data.totals.available }}
              </v-chip>
              <v-chip
                v-if="data.totals.on_order > 0"
                color="purple" variant="tonal"
                prepend-icon="mdi-cart-arrow-down"
              >
                On Order: {{ data.totals.on_order }}
              </v-chip>
              <v-spacer />
              <div v-if="data.part.manufacturer" class="text-caption text-secondary">
                {{ data.part.manufacturer }}
              </div>
            </div>
          </v-card>

          <!-- Stocks per warehouse -->
          <v-card flat>
            <v-card-title class="text-subtitle-2 pa-2 d-flex align-center"
                           style="background: rgba(0,0,0,0.02)">
              <v-icon size="18" class="me-1">mdi-warehouse</v-icon>
              Stock by warehouse
            </v-card-title>
            <v-divider />
            <v-table density="compact" v-if="data.stocks.length">
              <thead>
                <tr>
                  <th>Warehouse</th>
                  <th class="text-right">On Hand</th>
                  <th class="text-right">Reserved</th>
                  <th class="text-right">Available</th>
                  <th class="text-right">Last cost</th>
                  <th>Reserved for / Last used</th>
                  <th class="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="s in data.stocks" :key="s.inventory_id">
                  <td>
                    <strong>{{ s.warehouse_name || s.warehouse_code }}</strong>
                    <div class="text-caption text-secondary">
                      {{ s.warehouse_code }}
                    </div>
                  </td>
                  <td class="text-right"><strong>{{ s.quantity_on_hand }}</strong></td>
                  <td class="text-right">
                    <span :class="{ 'text-warning': s.quantity_reserved > 0 }">
                      {{ s.quantity_reserved }}
                    </span>
                  </td>
                  <td class="text-right">
                    <v-chip
                      :color="s.quantity_available > 0 ? 'success' : 'error'"
                      size="x-small" variant="tonal"
                    >
                      {{ s.quantity_available }}
                    </v-chip>
                  </td>
                  <td class="text-right">
                    <span v-if="s.last_cost">${{ Number(s.last_cost).toFixed(2) }}</span>
                    <span v-else class="text-secondary">&mdash;</span>
                  </td>
                  <td class="text-caption">
                    <div v-if="s.reserved_for_work_order_id">
                      <v-icon size="12" color="amber">mdi-lock-clock</v-icon>
                      reserved for job #{{ s.reserved_for_work_order_id }}
                    </div>
                    <div v-if="s.last_used_work_order_id">
                      <v-icon size="12" color="grey">mdi-history</v-icon>
                      last used on #{{ s.last_used_work_order_id }}
                    </div>
                  </td>
                  <td class="text-right text-no-wrap">
                    <v-tooltip text="Reserve for a job">
                      <template #activator="{ props: tp }">
                        <v-btn v-bind="tp" icon="mdi-lock-clock"
                               size="x-small" variant="text"
                               :disabled="s.quantity_available <= 0"
                               @click="openReserve(s)" />
                      </template>
                    </v-tooltip>
                    <v-tooltip text="Transfer / ship to another warehouse">
                      <template #activator="{ props: tp }">
                        <v-btn v-bind="tp" icon="mdi-truck-delivery"
                               size="x-small" variant="text"
                               :disabled="s.quantity_available <= 0"
                               @click="openTransfer(s)" />
                      </template>
                    </v-tooltip>
                    <v-tooltip text="Adjust stock / cost">
                      <template #activator="{ props: tp }">
                        <v-btn v-bind="tp" icon="mdi-pencil"
                               size="x-small" variant="text"
                               @click="openAdjust(s)" />
                      </template>
                    </v-tooltip>
                    <v-tooltip text="Movement history">
                      <template #activator="{ props: tp }">
                        <v-btn v-bind="tp" icon="mdi-history"
                               size="x-small" variant="text"
                               @click="openMovements(s)" />
                      </template>
                    </v-tooltip>
                  </td>
                </tr>
              </tbody>
            </v-table>
            <div v-else class="pa-3 text-center text-secondary">
              No stock on any warehouse.
            </div>
          </v-card>

          <!-- Linked jobs -->
          <v-card flat v-if="data.linked_jobs.length">
            <v-card-title class="text-subtitle-2 pa-2"
                           style="background: rgba(0,0,0,0.02)">
              <v-icon size="18" class="me-1">mdi-clipboard-list</v-icon>
              Linked jobs ({{ data.linked_jobs.length }})
            </v-card-title>
            <v-divider />
            <v-table density="compact">
              <thead>
                <tr>
                  <th>Job</th>
                  <th>Job status</th>
                  <th>Link kind</th>
                  <th class="text-right">Qty</th>
                  <th>Line status</th>
                  <th>Linked at</th>
                  <th class="text-right">Open</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(j, i) in data.linked_jobs" :key="i">
                  <td><strong>#{{ j.job_number }}</strong></td>
                  <td>{{ j.job_status || '-' }}</td>
                  <td>{{ j.link_kind }}</td>
                  <td class="text-right">{{ j.quantity }}</td>
                  <td>{{ j.line_status }}</td>
                  <td class="text-caption">{{ formatDate(j.linked_at) }}</td>
                  <td class="text-right">
                    <v-btn size="x-small" variant="text"
                           icon="mdi-open-in-new"
                           @click="openJob(j.work_order_id)" />
                  </td>
                </tr>
              </tbody>
            </v-table>
          </v-card>

          <!-- Movements -->
          <v-card flat>
            <v-card-title class="text-subtitle-2 pa-2 d-flex align-center"
                           style="background: rgba(0,0,0,0.02)">
              <v-icon size="18" class="me-1">mdi-history</v-icon>
              Recent movements (all warehouses)
              <v-spacer />
              <v-tooltip
                text="Tamper-evident, append-only journal. Every row is sealed with a SHA-256 hash chain. Open the full Audit Journal to verify chain integrity."
                location="top"
              >
                <template v-slot:activator="{ props: tp }">
                  <v-icon size="14" color="success" class="me-1" v-bind="tp">
                    mdi-shield-check
                  </v-icon>
                </template>
              </v-tooltip>
              <v-btn
                v-if="canViewAudit"
                size="x-small"
                variant="text"
                prepend-icon="mdi-shield-lock-outline"
                @click="openFullAudit"
              >Full audit journal</v-btn>
            </v-card-title>
            <v-divider />
            <v-table density="compact" v-if="data.movements.length">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Warehouse</th>
                  <th class="text-right">Qty</th>
                  <th class="text-right">After</th>
                  <th>Reference</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="m in data.movements.slice(0, 25)" :key="m.id">
                  <td class="text-no-wrap text-caption">{{ formatDate(m.movement_date) }}</td>
                  <td>
                    <v-chip size="x-small" variant="tonal">{{ m.movement_type }}</v-chip>
                  </td>
                  <td class="text-caption">{{ m.warehouse_code }}</td>
                  <td class="text-right" :class="m.quantity >= 0 ? 'text-success' : 'text-error'">
                    {{ m.quantity > 0 ? '+' : '' }}{{ m.quantity }}
                  </td>
                  <td class="text-right">{{ m.quantity_after }}</td>
                  <td class="text-caption">{{ m.reference_number || '' }}</td>
                  <td class="text-caption">{{ m.notes || '' }}</td>
                </tr>
              </tbody>
            </v-table>
            <div v-else class="pa-3 text-center text-secondary text-body-2">
              No movements yet.
            </div>
          </v-card>
        </div>
      </v-card-text>

      <v-divider />
      <v-card-actions class="pa-3">
        <span v-if="data?.part?.is_active === false"
              class="text-error text-body-2 me-2">
          Inactive in catalog
        </span>
        <v-spacer />
        <v-btn variant="text" @click="close">Close</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Nested action dialogs -->
    <InventoryAdjustDialog
      v-model="adjustOpen"
      :item="actionTarget"
      @saved="reload"
    />
    <InventoryMovementsDialog
      v-model="movementsOpen"
      :item="actionTarget"
    />
    <ReserveDialog
      v-model="reserveOpen"
      :item="actionTarget"
      @saved="reload"
    />
    <TransferDialog
      v-model="transferOpen"
      :item="actionTarget"
      @saved="reload"
    />
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/api/client'
import { useNotify } from '@/composables/useNotify'
import InventoryAdjustDialog from './InventoryAdjustDialog.vue'
import InventoryMovementsDialog from './InventoryMovementsDialog.vue'
import ReserveDialog from './ReserveDialog.vue'
import TransferDialog from './TransferDialog.vue'

const router = useRouter()
const { notify } = useNotify()

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  partId: { type: [Number, String], default: null },
})
const emit = defineEmits(['update:modelValue'])
const visible = computed({
  get: () => props.modelValue,
  set: v => emit('update:modelValue', v),
})

const loading = ref(false)
const data = ref(null)
const canViewAudit = ref(false)

const adjustOpen = ref(false)
const movementsOpen = ref(false)
const reserveOpen = ref(false)
const transferOpen = ref(false)
const actionTarget = ref(null)

async function load() {
  if (!props.partId) return
  loading.value = true
  data.value = null
  try {
    const r = await axios.get(`/parts/lookup/${props.partId}/`)
    data.value = r.data
  } catch (e) {
    console.error('[PartDetailDialog] load failed', e)
    notify.error('Could not load part details')
  } finally {
    loading.value = false
  }
  axios.get('/parts/audit/who_can_view/')
    .then(r => { canViewAudit.value = !!r.data?.can_view })
    .catch(() => { canViewAudit.value = false })
}

function openFullAudit() {
  if (!props.partId) return
  router.push({ name: 'PartsAudit', query: { part_id: props.partId } })
  visible.value = false
}

function buildActionTarget(s) {
  return {
    id: s.inventory_id,
    part_id: data.value?.part?.id,
    part_number: data.value?.part?.part_number,
    part_name: data.value?.part?.name,
    warehouse_code: s.warehouse_code,
    warehouse_name: s.warehouse_name,
    warehouse_id: s.warehouse_id,
    quantity_on_hand: s.quantity_on_hand,
    quantity_reserved: s.quantity_reserved,
    quantity_available: s.quantity_available,
    last_cost: s.last_cost,
  }
}

function openAdjust(s)     { actionTarget.value = buildActionTarget(s); adjustOpen.value = true }
function openMovements(s)  { actionTarget.value = buildActionTarget(s); movementsOpen.value = true }
function openReserve(s)    { actionTarget.value = buildActionTarget(s); reserveOpen.value = true }
function openTransfer(s)   { actionTarget.value = buildActionTarget(s); transferOpen.value = true }

function reload() { load() }

function openJob(id) {
  if (!id) return
  router.push(`/jobs/${id}`)
  visible.value = false
}

function formatDate(s) {
  if (!s) return ''
  try {
    return new Date(s).toLocaleString(undefined, {
      year: '2-digit', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    })
  } catch { return s }
}

watch(() => props.modelValue, v => { if (v) load() })
watch(() => props.partId, () => { if (visible.value) load() })

function close() { visible.value = false }
</script>
